import { OfferForm } from "./offer.schema";
import { Card } from "@/src/components/wrappers/Card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";
import { Switch } from "../../../components/ui/switch";
import { ChangeEvent, useEffect, useState } from "react";
import { useToast } from "../../../components/ui/use-toast";
import { User } from "../model";
import { useFormContext } from "react-hook-form";
import { useFetch } from "../../../lib/useFetch";
import { useEmit } from "../../../lib/actions";
import { useDebounce } from "../../../lib/useDebounce";
import { logBasicFieldChange, useOfferCreatorBlurLogger } from "../offerCreatorLogs";

export function Customer({ offerId }: { offerId?: string }) {
  const [showFields, setShowFields] = useState(false);
  const { setValue, getValues } = useFormContext<OfferForm>();
  const emitDisable = useEmit<boolean>("offer:disable-next");
  const { toast } = useToast();
  const { fetch } = useFetch();
  const { captureFieldFocus, logBasicFieldBlur } = useOfferCreatorBlurLogger();

  async function fetchUser(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.value) return false;
    await fetch<User>(`/api/auth/user?email=${encodeURIComponent(e.target.value)}`)
      .then((r) => {
        if (r.role === "broker") {
          toast({
            title: "Customer is broker",
            description: "Customer can't be a broker",
            variant: "destructive",
          });
          setValue("customer.email", "", { shouldValidate: true });
          setValue("customer.firstName", "", { shouldDirty: true });
          setValue("customer.lastName", "", { shouldDirty: true });
          setValue("customer.mobile", "", { shouldDirty: true });
        } else {
          setValue("customer.firstName", r.firstName, { shouldValidate: true });
          setValue("customer.lastName", r.lastName, { shouldValidate: true });
          setValue("customer.mobile", r.mobile, { shouldValidate: true });
          setValue("customer.createDeal", false, { shouldValidate: true });
        }
        emitDisable(false);
      })
      .catch(() => {
        // Not in our DB = a new customer (e.g. a PPC lead). This is normal, not
        // an error — keep whatever details are already filled (e.g. carried over
        // from the HubSpot deal); the customer is created on offer save. Don't
        // wipe firstName/lastName/mobile here, that was destroying prefilled data.
        toast({
          title: "New customer",
          description: "This email isn't in the database yet — it will be created with the offer.",
        });
        emitDisable(false);
      });
    setShowFields(true);
  }

  const debouncedEmit = useDebounce(fetchUser, 1000);

  useEffect(() => {
    const email = getValues("customer.email");
    if (email) {
      setShowFields(true);
    }
  }, []);

  // "Select a client" picker (Phase 1 prototype): searchable customer list
  // with charter context; picking one prefills the form below.
  const [pickerQ, setPickerQ] = useState("");
  const [pickerResults, setPickerResults] = useState<any[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      fetch<{ data: any[] }>(`/api/offers/workspace/clients?q=${encodeURIComponent(pickerQ)}`)
        .then((r) => !cancelled && setPickerResults(r.data ?? []))
        .catch(() => !cancelled && setPickerResults([]));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerQ]);

  const pickClient = (c: any) => {
    setValue("customer.email", c.email ?? "", { shouldValidate: true });
    setValue("customer.firstName", c.firstName ?? "", { shouldValidate: true });
    setValue("customer.lastName", c.lastName ?? "", { shouldValidate: true });
    setValue("customer.mobile", c.mobile ?? "", { shouldValidate: true });
    setValue("customer.createDeal", false, { shouldValidate: true });
    setShowFields(true);
  };

  const contextLine = (c: any) => {
    const bits = [
      c.charters ? `Returning · ${c.charters} charter${c.charters === 1 ? "" : "s"}` : "New enquiry",
      c.lastRegion,
      c.lastGuests ? `${c.lastGuests} guests` : null,
      c.lastStart
        ? new Date(c.lastStart).toLocaleDateString("en-GB", { month: "short", year: undefined })
        : null,
    ].filter(Boolean);
    return bits.join(" · ");
  };

  return (
    <div className="flex flex-col flex-grow gap-6">
      <Card>
        <div className="flex flex-col max-w-[1016px]">
          <h3 className="text-lg font-semibold col-span-full mb-4">Customer</h3>
          <h5 className="text-sm col-span-full mb-6">Pick an existing client or add a new one.</h5>
          <input
            className="border rounded-md border-blue-100 p-2.5 text-sm mb-2 w-full"
            placeholder="Search clients or start typing a new name…"
            value={pickerQ}
            onChange={(e) => setPickerQ(e.target.value)}
          />
          {pickerResults !== null && (
            <div className="flex flex-col divide-y divide-blue-50 border border-blue-100 rounded-md mb-5 max-h-[260px] overflow-y-auto">
              {pickerResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pickClient(c)}
                  className="flex items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-50/60"
                >
                  <span className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center shrink-0">
                    {(c.firstName?.[0] ?? "") + (c.lastName?.[0] ?? "")}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium truncate">
                      {c.fullName || [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email}
                    </span>
                    <span className="block text-xs text-slate-500 truncate">{contextLine(c)}</span>
                  </span>
                  <span className="ml-auto text-slate-300">→</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowFields(true)}
                className="px-3 py-2.5 text-sm text-blue-600 text-left hover:bg-blue-50/60"
              >
                + Add a new client
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-6 w-full">
            <FormField<OfferForm, "customer.email">
              name="customer.email"
              render={({ field: { onChange, onBlur, value } }) => (
                <FormItem className="flex flex-col" required>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter customer's email"
                      onFocus={() => captureFieldFocus("customer.email", value)}
                      onChange={(e) => {
                        emitDisable(true);
                        onChange(e);
                        debouncedEmit(e);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        logBasicFieldBlur({
                          offerId,
                          field: "customer.email",
                          before: value,
                          after: e.currentTarget.value,
                        });
                      }}
                      value={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField<OfferForm, "customer.mobile">
              name="customer.mobile"
              render={({ field: { onChange, onBlur, value } }) => (
                <FormItem className={`flex flex-col ${showFields ? "" : "hidden"}`} required>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter customer's phone"
                      onFocus={() => captureFieldFocus("customer.mobile", value)}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        logBasicFieldBlur({
                          offerId,
                          field: "customer.mobile",
                          before: value,
                          after: e.currentTarget.value,
                        });
                      }}
                      value={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField<OfferForm, "customer.firstName">
              name="customer.firstName"
              render={({ field: { onChange, onBlur, value } }) => (
                <FormItem className={`flex flex-col ${showFields ? "" : "hidden"}`} required>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter customer's name"
                      onFocus={() => captureFieldFocus("customer.firstName", value)}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        logBasicFieldBlur({
                          offerId,
                          field: "customer.firstName",
                          before: value,
                          after: e.currentTarget.value,
                        });
                      }}
                      value={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField<OfferForm, "customer.lastName">
              name="customer.lastName"
              render={({ field: { onChange, onBlur, value } }) => (
                <FormItem className={`flex flex-col ${showFields ? "" : "hidden"}`}>
                  <FormLabel>Surname</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter customer's surname"
                      onFocus={() => captureFieldFocus("customer.lastName", value)}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      onBlur={(e) => {
                        onBlur();
                        logBasicFieldBlur({
                          offerId,
                          field: "customer.lastName",
                          before: value,
                          after: e.currentTarget.value,
                        });
                      }}
                      value={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField<OfferForm, "customer.createDeal">
              name="customer.createDeal"
              render={({ field: { onChange, value } }) => (
                <FormItem className={`flex flex-col ${showFields ? "" : "hidden"}`} required>
                  <FormLabel>Create new Deal in Hubspot</FormLabel>
                  <FormControl>
                    <Switch
                      onCheckedChange={(checked) => {
                        logBasicFieldChange({
                          offerId,
                          field: "customer.createDeal",
                          before: value,
                          after: checked,
                        });
                        onChange(checked);
                      }}
                      checked={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </Card>
      <Card>
        <div className="flex flex-col max-w-[1016px]">
          <h3 className="text-lg font-semibold col-span-full mb-4">Mailing Settings</h3>
          <div className="flex flex-col flex-grow gap-6 w-full">
            <FormField<OfferForm, "mailing.bookingConfirmation">
              name="mailing.bookingConfirmation"
              render={({ field: { onChange, value } }) => {
                return (
                  <FormItem className={`flex items-center gap-6`} required>
                    <FormLabel>Create booking notification:</FormLabel>
                    <FormControl>
                      <Switch
                        onCheckedChange={(checked) => {
                          logBasicFieldChange({
                            offerId,
                            field: "mailing.bookingConfirmation",
                            before: value,
                            after: checked,
                          });
                          onChange(checked);
                        }}
                        checked={value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField<OfferForm, "mailing.bookingUpdates">
              name="mailing.bookingUpdates"
              render={({ field: { onChange, value } }) => (
                <FormItem className={`flex items-center gap-6`} required>
                  <FormLabel>Booking updates notifications:</FormLabel>
                  <FormControl>
                    <Switch
                      onCheckedChange={(checked) => {
                        logBasicFieldChange({
                          offerId,
                          field: "mailing.bookingUpdates",
                          before: value,
                          after: checked,
                        });
                        onChange(checked);
                      }}
                      checked={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField<OfferForm, "mailing.paymentConfirmations">
              name="mailing.paymentConfirmations"
              render={({ field: { onChange, value } }) => (
                <FormItem className={`flex items-center gap-6`} required>
                  <FormLabel>
                    Payment confirmation notifications (for manually handled payments in HELMs App):
                  </FormLabel>
                  <FormControl>
                    <Switch
                      onCheckedChange={(checked) => {
                        logBasicFieldChange({
                          offerId,
                          field: "mailing.paymentConfirmations",
                          before: value,
                          after: checked,
                        });
                        onChange(checked);
                      }}
                      checked={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField<OfferForm, "mailing.paymentReminders">
              name="mailing.paymentReminders"
              render={({ field: { onChange, value } }) => (
                <FormItem className={`flex items-center gap-6`} required>
                  <FormLabel>Payment reminders notifications:</FormLabel>
                  <FormControl>
                    <Switch
                      onCheckedChange={(checked) => {
                        logBasicFieldChange({
                          offerId,
                          field: "mailing.paymentReminders",
                          before: value,
                          after: checked,
                        });
                        onChange(checked);
                      }}
                      checked={value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
